import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api, notifyCreatorApprovalPending } from '../api';
import { logHttpError } from '../utils/httpError';
import {
  Paper, Typography, Box, CircularProgress, Button, Alert, Grid, Chip, Divider,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PageHeader from './common/PageHeader';
import LoadingBlock from './common/LoadingBlock';
import { Link } from 'react-router-dom';
import { getSocialLink, platformLabel, platformColor } from '../utils/socialLinks';

export default function UserApproval() {
  const theme = useTheme();
  const [creators, setCreators] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Per-item action tracking
  const [processing, setProcessing] = useState(new Set());
  const processingRef = useRef(new Set());
  const [results, setResults] = useState({}); // keyed "image:<id>" or "handle:<id>"

  // Text-field approval state (local only)
  const [bioApproved, setBioApproved] = useState(null);       // null | 'approved' | 'rejected'
  const [icExpApproved, setIcExpApproved] = useState(null);
  const [btcExpApproved, setBtcExpApproved] = useState(null);

  // Creator-level action result
  const [creatorResult, setCreatorResult] = useState(null);

  // Validation alert
  const [validationMsg, setValidationMsg] = useState('');

  // ---- Data fetch ----
  const fetchCreators = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/pending-creators');
      const data = res.data?.data || [];
      setCreators(data);
      setCurrentIndex(0);
      resetLocalState();
      notifyCreatorApprovalPending(data.length > 0);
    } catch (err) {
      logHttpError(err, 'Fetch pending creators');
      setError('Failed to fetch pending creators. Please try refreshing.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCreators(); }, [fetchCreators]);

  function resetLocalState() {
    setProcessing(new Set());
    processingRef.current = new Set();
    setResults({});
    setBioApproved(null);
    setIcExpApproved(null);
    setBtcExpApproved(null);
    setCreatorResult(null);
    setValidationMsg('');
  }

  // ---- Current creator (normalize API field names) ----
  const rawCreator = creators[currentIndex];
  const creator = rawCreator ? {
    ...rawCreator,
    user_id: rawCreator.id ?? rawCreator.user_id,
    instant_connect_expectations: rawCreator.instant_connect_connection_expectations ?? rawCreator.instant_connect_expectations,
    bid_to_connect_expectations: rawCreator.bid_to_connect_connection_expectations ?? rawCreator.bid_to_connect_expectations,
  } : null;

  // ---- Navigation ----
  const goNext = useCallback(() => {
    resetLocalState();
    setCurrentIndex(prev => prev + 1);
  }, []);

  // ---- Generic processing helper ----
  function startProcessing(key) {
    if (processingRef.current.has(key)) return false;
    processingRef.current = new Set([...processingRef.current, key]);
    setProcessing(new Set(processingRef.current));
    return true;
  }
  function stopProcessing(key) {
    processingRef.current = new Set([...processingRef.current].filter(k => k !== key));
    setProcessing(new Set(processingRef.current));
  }

  // ---- Image actions ----
  const handleImageAction = useCallback(async (imageId, action) => {
    const key = `image:${imageId}`;
    if (!startProcessing(key)) return;
    try {
      if (action === 'approve') {
        await api.post(`/admin/approve-user-image/${imageId}`);
      } else {
        await api.post(`/admin/reject-user-image/${imageId}`);
      }
      setResults(prev => ({ ...prev, [key]: { success: true, action } }));
    } catch (err) {
      const details = logHttpError(err, `${action} image`);
      const msg = details?.data?.message || err?.message || `Failed to ${action} image`;
      setResults(prev => ({ ...prev, [key]: { success: false, message: msg } }));
    } finally {
      stopProcessing(key);
    }
  }, []);

  // ---- Handle actions ----
  const handleHandleAction = useCallback(async (handleId, action) => {
    const key = `handle:${handleId}`;
    if (!startProcessing(key)) return;
    try {
      if (action === 'approve') {
        await api.post('/admin/approve-social-media-handle', { handle_id: handleId });
      } else {
        await api.post('/admin/reject-social-media-handle', { handle_id: handleId });
      }
      setResults(prev => ({ ...prev, [key]: { success: true, action } }));
    } catch (err) {
      const details = logHttpError(err, `${action} handle`);
      const msg = details?.data?.message || err?.message || `Failed to ${action} handle`;
      setResults(prev => ({ ...prev, [key]: { success: false, message: msg } }));
    } finally {
      stopProcessing(key);
    }
  }, []);

  // ---- Text field reject actions (hit API) ----
  const handleRejectBio = useCallback(async () => {
    const key = 'bio';
    if (!startProcessing(key)) return;
    try {
      await api.post('/admin/clear-short-bio', { user_id: creator.user_id });
      setBioApproved('rejected');
    } catch (err) {
      logHttpError(err, 'Clear short bio');
      setError('Failed to clear bio.');
    } finally {
      stopProcessing(key);
    }
  }, [creator]);

  const handleRejectIcExp = useCallback(async () => {
    const key = 'icExp';
    if (!startProcessing(key)) return;
    try {
      await api.post('/admin/clear-instant-connect-expectations', { user_id: creator.user_id });
      setIcExpApproved('rejected');
    } catch (err) {
      logHttpError(err, 'Clear IC expectations');
      setError('Failed to clear instant connect expectations.');
    } finally {
      stopProcessing(key);
    }
  }, [creator]);

  const handleRejectBtcExp = useCallback(async () => {
    const key = 'btcExp';
    if (!startProcessing(key)) return;
    try {
      await api.post('/admin/clear-bid-to-connect-expectations', { user_id: creator.user_id });
      setBtcExpApproved('rejected');
    } catch (err) {
      logHttpError(err, 'Clear BTC expectations');
      setError('Failed to clear bid to connect expectations.');
    } finally {
      stopProcessing(key);
    }
  }, [creator]);

  // ---- Validation ----
  function validate() {
    if (!creator) return '';
    const vImages = creator.images || [];
    const vRawHandles = creator.socialMediaHandles || creator.social_handles || [];
    const vHandles = vRawHandles.map(h => ({
      ...h,
      status: h.status || (h.is_approved === 1 || h.is_approved === '1' || h.is_approved === 'approved' ? 'approved' : 'pending'),
    }));

    const pendingImages = vImages.filter(img => {
      const s = img.approval_status || img.status || img.is_approved || 'pending';
      return s === 'pending';
    });
    const unreviewedImages = pendingImages.filter(img => !results[`image:${img.id}`]);
    if (unreviewedImages.length > 0) return 'All pending images must be approved or rejected.';

    const approvedImages = vImages.filter(img => {
      const r = results[`image:${img.id}`];
      const s = img.approval_status || img.status || img.is_approved || 'pending';
      return s === 'approved' || (r?.success && r.action === 'approve');
    });
    if (approvedImages.length === 0) return 'At least one image must be approved.';

    const pendingHandles = vHandles.filter(h => h.status === 'pending');
    const unreviewedHandles = pendingHandles.filter(h => !results[`handle:${h.id}`]);
    if (unreviewedHandles.length > 0) return 'All pending social handles must be approved or rejected.';

    if (creator.short_bio && bioApproved === null) return 'Bio must be approved or rejected.';

    const hasIcExp = !!creator.instant_connect_expectations;
    const hasBtcExp = !!creator.bid_to_connect_expectations;
    if (hasIcExp && icExpApproved === null) return 'Instant connect expectations must be approved or rejected.';
    if (hasBtcExp && btcExpApproved === null) return 'Bid to connect expectations must be approved or rejected.';

    // At least one connection expectation must be approved
    if (hasIcExp || hasBtcExp) {
      const icOk = icExpApproved === 'approved';
      const btcOk = btcExpApproved === 'approved';
      if (!icOk && !btcOk) return 'At least one connection expectation must be approved.';
    }

    return '';
  }

  // ---- Creator-level actions ----
  const handleApproveCreator = useCallback(async () => {
    const msg = validate();
    if (msg) { setValidationMsg(msg); return; }
    setValidationMsg('');
    const key = 'creator';
    if (!startProcessing(key)) return;
    try {
      const res = await api.post('/admin/approve-creator', { user_id: creator.user_id });
      setCreatorResult({ success: true, message: res.data?.message || 'Creator approved' });
    } catch (err) {
      const details = logHttpError(err, 'Approve creator');
      const errMsg = details?.data?.message || err?.message || 'Failed to approve creator';
      setCreatorResult({ success: false, message: errMsg });
    } finally {
      stopProcessing(key);
    }
  }, [creator, results, bioApproved, icExpApproved, btcExpApproved]);

  const handleRejectCreator = useCallback(async () => {
    const key = 'creator';
    if (!startProcessing(key)) return;
    try {
      const res = await api.post('/admin/reject-creator', { user_id: creator.user_id });
      setCreatorResult({ success: true, message: res.data?.message || 'Creator rejected' });
    } catch (err) {
      const details = logHttpError(err, 'Reject creator');
      const errMsg = details?.data?.message || err?.message || 'Failed to reject creator';
      setCreatorResult({ success: false, message: errMsg });
    } finally {
      stopProcessing(key);
    }
  }, [creator]);

  // ---- Keyboard shortcuts ----
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key.toLowerCase() === 'n') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext]);

  // ---- Render ----
  if (loading) return <LoadingBlock height={420} />;

  if (error && creators.length === 0) {
    return (
      <>
        <PageHeader title="Approve Creators" />
        <Paper sx={{ p: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Paper>
      </>
    );
  }

  if (creators.length === 0 || currentIndex >= creators.length) {
    return (
      <>
        <PageHeader title="Approve Creators" />
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5">All creators have been reviewed!</Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={fetchCreators}>Refresh</Button>
        </Paper>
      </>
    );
  }

  const images = creator.images || [];
  const rawHandles = creator.socialMediaHandles || creator.social_handles || [];
  // Normalize: API uses is_approved (0/1), frontend expects status string
  const handles = rawHandles.map(h => ({
    ...h,
    status: h.status || (h.is_approved === 1 || h.is_approved === '1' || h.is_approved === 'approved' ? 'approved' : 'pending'),
  }));
  const verificationVideo = creator.humanVerificationVideos?.[0];
  const niches = creator.niches || [];

  // Check if approval is impossible due to rejected required items.
  // Only images rejected this session count — untouched pending images can
  // still be approved, so they must not flip the page to Reject-only.
  const allImagesRejected = images.length > 0 && images.every(img => {
    const r = results[`image:${img.id}`];
    return r?.success && r.action === 'reject';
  });
  // Defensive only — the backend now excludes zero-image creators from the
  // pending-creators queue; still needed while older backends are deployed.
  const noImagesAtAll = images.length === 0;
  const bioRejected = creator.short_bio && bioApproved === 'rejected';
  const bioMissing = !creator.short_bio;
  const icExpRejected = creator.instant_connect_expectations && icExpApproved === 'rejected';
  const btcExpRejected = creator.bid_to_connect_expectations && btcExpApproved === 'rejected';
  const icExpMissing = !creator.instant_connect_expectations;
  const btcExpMissing = !creator.bid_to_connect_expectations;
  // Both expectations are gone (rejected or never existed)
  const noExpectationsLeft = (icExpRejected || icExpMissing) && (btcExpRejected || btcExpMissing);

  const approvalImpossible = noImagesAtAll || allImagesRejected || bioRejected || bioMissing || noExpectationsLeft;

  // Explain WHY only Reject Creator is offered, so admins don't waste effort
  // approving fields for a creator that can never be approved.
  const impossibleReasons = [];
  if (noImagesAtAll) impossibleReasons.push('This creator has no active profile images, and approval requires at least one approved profile image. Use Reject Creator to prompt them to complete their profile.');
  if (allImagesRejected) impossibleReasons.push('All profile images were rejected. The creator has been notified to upload a new photo and will leave this queue until they do.');
  if (bioMissing) impossibleReasons.push('This creator has no bio — approval requires a bio.');
  if (bioRejected) impossibleReasons.push('The bio was rejected and cleared — approval requires a bio.');
  if (noExpectationsLeft) impossibleReasons.push('No connection expectations remain (rejected or missing) — approval requires at least one.');

  return (
    <>
      <PageHeader title="Approve Creators" />
      <Paper sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* If creator-level action completed, show result */}
        {creatorResult ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Alert severity={creatorResult.success ? 'success' : 'error'} sx={{ mb: 3 }}>
              {creatorResult.message}
            </Alert>
            <Button size="large" variant="contained" onClick={goNext}>Next</Button>
          </Box>
        ) : (
          <>
            {/* 1. User info bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6">
                Creator:{' '}
                <Link
                  to={`/user/${creator.user_id}`}
                  state={{ from: { label: 'Approve Creators', path: '/admin/user-approval' } }}
                  style={{ textDecoration: 'none', color: theme.palette.primary.main, fontWeight: 700 }}
                >
                  {creator.username || `User #${creator.user_id}`}
                </Link>
              </Typography>
              <Typography variant="body2" color="text.secondary">ID: {creator.user_id}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                Creator {currentIndex + 1} of {creators.length}
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* 2. Profile Images */}
            {images.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Profile Images</Typography>
                <Grid container spacing={2}>
                  {images.map((img) => {
                    const key = `image:${img.id}`;
                    const result = results[key];
                    const imgStatus = img.approval_status || img.status || img.is_approved || 'pending';
                    const isPending = imgStatus === 'pending';
                    const isProc = processing.has(key);

                    return (
                      <Grid item xs={6} sm={4} md={3} key={img.id}>
                        <Box sx={{ position: 'relative', opacity: result?.success ? 0.5 : 1, transition: 'opacity 0.3s' }}>
                          {img.secureUrl ? (
                            <Box
                              component="img"
                              src={img.secureUrl}
                              alt={`Profile ${img.id}`}
                              sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 1, bgcolor: '#f0f0f0' }}
                            />
                          ) : (
                            <Box sx={{ width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f0f0', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary">N/A</Typography>
                            </Box>
                          )}
                          <Chip
                            label={result?.success ? (result.action === 'approve' ? 'Approved' : 'Rejected') : (isPending ? 'Pending' : imgStatus)}
                            size="small"
                            color={
                              result?.success
                                ? (result.action === 'approve' ? 'success' : 'error')
                                : (isPending ? 'warning' : 'success')
                            }
                            sx={{ position: 'absolute', top: 4, right: 4 }}
                          />
                          {isPending && !result && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                              <Button size="small" variant="contained" color="success" fullWidth onClick={() => handleImageAction(img.id, 'approve')} disabled={isProc}>
                                {isProc ? <CircularProgress size={16} /> : 'Approve'}
                              </Button>
                              <Button size="small" variant="contained" color="error" fullWidth onClick={() => handleImageAction(img.id, 'reject')} disabled={isProc}>
                                {isProc ? <CircularProgress size={16} /> : 'Reject'}
                              </Button>
                            </Box>
                          )}
                          {result && !result.success && (
                            <Alert severity="error" sx={{ mt: 0.5, py: 0 }}>{result.message}</Alert>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* 3. Verification Video */}
            {verificationVideo && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Verification Video</Typography>
                {verificationVideo.secureUrl ? (
                  <Box
                    component="video"
                    src={verificationVideo.secureUrl}
                    autoPlay
                    loop
                    muted
                    sx={{ width: '100%', maxHeight: 400, objectFit: 'contain', bgcolor: '#000', borderRadius: 1 }}
                  />
                ) : (
                  <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f0f0', borderRadius: 1 }}>
                    <Typography color="text.secondary">Video not available</Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* 4. Social Media Handles */}
            {handles.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Social Media Handles</Typography>
                <Grid container spacing={2}>
                  {handles.map((h) => {
                    const key = `handle:${h.id}`;
                    const result = results[key];
                    const isPending = h.status === 'pending';
                    const isProc = processing.has(key);
                    const { url, problem } = getSocialLink(h.type, h.handle);

                    return (
                      <Grid item xs={12} sm={6} md={4} key={h.id}>
                        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, opacity: result?.success ? 0.5 : 1, transition: 'opacity 0.3s' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Chip
                              label={platformLabel(h.type)}
                              size="small"
                              sx={{ bgcolor: platformColor(h.type), color: '#fff', fontWeight: 600 }}
                            />
                            {result?.success ? (
                              <Chip label={result.action === 'approve' ? 'Approved' : 'Rejected'} size="small" color={result.action === 'approve' ? 'success' : 'error'} />
                            ) : isPending ? (
                              <Chip label="Pending" size="small" color="warning" />
                            ) : (
                              <Chip label={h.status} size="small" color="success" />
                            )}
                          </Box>
                          {url ? (
                            <Typography
                              component="a"
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ fontSize: '1rem', fontWeight: 600, color: theme.palette.primary.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {h.handle}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>{h.handle}</Typography>
                          )}
                          {problem && isPending && !result && (
                            <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{problem}</Alert>
                          )}
                          {isPending && !result && (
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                              <Button size="small" variant="contained" color="success" fullWidth onClick={() => handleHandleAction(h.id, 'approve')} disabled={isProc}>
                                {isProc ? <CircularProgress size={16} /> : 'Approve'}
                              </Button>
                              <Button size="small" variant="contained" color="error" fullWidth onClick={() => handleHandleAction(h.id, 'reject')} disabled={isProc}>
                                {isProc ? <CircularProgress size={16} /> : 'Reject'}
                              </Button>
                            </Box>
                          )}
                          {result && !result.success && (
                            <Alert severity="error" sx={{ mt: 0.5, py: 0 }}>{result.message}</Alert>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {/* 5. Bio */}
            {creator.short_bio && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Bio</Typography>
                <Typography sx={{ px: 2, py: 1.5, bgcolor: alpha('#f5f5f5', 0.5), borderRadius: 1, fontStyle: 'italic', mb: 1 }}>
                  "{creator.short_bio}"
                </Typography>
                {bioApproved ? (
                  <Chip label={bioApproved === 'approved' ? 'Approved' : 'Rejected (cleared)'} size="small" color={bioApproved === 'approved' ? 'success' : 'error'} />
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" color="success" onClick={() => setBioApproved('approved')} disabled={processing.has('bio')}>Approve</Button>
                    <Button size="small" variant="contained" color="error" onClick={handleRejectBio} disabled={processing.has('bio')}>
                      {processing.has('bio') ? <CircularProgress size={16} /> : 'Reject'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* 6. Instant Connect Expectations */}
            {creator.instant_connect_expectations && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Instant Connect Expectations</Typography>
                <Typography sx={{ px: 2, py: 1.5, bgcolor: alpha('#f5f5f5', 0.5), borderRadius: 1, fontStyle: 'italic', mb: 1 }}>
                  "{creator.instant_connect_expectations}"
                </Typography>
                {icExpApproved ? (
                  <Chip label={icExpApproved === 'approved' ? 'Approved' : 'Rejected (cleared)'} size="small" color={icExpApproved === 'approved' ? 'success' : 'error'} />
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" color="success" onClick={() => setIcExpApproved('approved')} disabled={processing.has('icExp')}>Approve</Button>
                    <Button size="small" variant="contained" color="error" onClick={handleRejectIcExp} disabled={processing.has('icExp')}>
                      {processing.has('icExp') ? <CircularProgress size={16} /> : 'Reject'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* 7. Bid to Connect Expectations */}
            {creator.bid_to_connect_expectations && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Bid to Connect Expectations</Typography>
                <Typography sx={{ px: 2, py: 1.5, bgcolor: alpha('#f5f5f5', 0.5), borderRadius: 1, fontStyle: 'italic', mb: 1 }}>
                  "{creator.bid_to_connect_expectations}"
                </Typography>
                {btcExpApproved ? (
                  <Chip label={btcExpApproved === 'approved' ? 'Approved' : 'Rejected (cleared)'} size="small" color={btcExpApproved === 'approved' ? 'success' : 'error'} />
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" color="success" onClick={() => setBtcExpApproved('approved')} disabled={processing.has('btcExp')}>Approve</Button>
                    <Button size="small" variant="contained" color="error" onClick={handleRejectBtcExp} disabled={processing.has('btcExp')}>
                      {processing.has('btcExp') ? <CircularProgress size={16} /> : 'Reject'}
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* 8. Niches */}
            {niches.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Niches</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {niches.map((niche, i) => (
                    <Chip key={i} label={niche.name || niche} variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}

            {/* 10. Validation Alert */}
            {validationMsg && (
              <Alert severity="warning" sx={{ mb: 2 }}>{validationMsg}</Alert>
            )}

            {/* Approval-impossible explanation */}
            {approvalImpossible && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Approve Creator is unavailable:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {impossibleReasons.map((reason, i) => (
                    <li key={i}>{reason}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {/* 9. Bottom action bar */}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              {!approvalImpossible && (
                <Button
                  size="large"
                  variant="contained"
                  color="success"
                  onClick={handleApproveCreator}
                  disabled={processing.has('creator')}
                >
                  {processing.has('creator') ? <CircularProgress size={24} /> : 'Approve Creator'}
                </Button>
              )}
              {approvalImpossible && (
                <Button
                  size="large"
                  variant="contained"
                  color="error"
                  onClick={handleRejectCreator}
                  disabled={processing.has('creator')}
                >
                  {processing.has('creator') ? <CircularProgress size={24} /> : 'Reject Creator'}
                </Button>
              )}
              <Button size="large" variant="outlined" onClick={goNext}>
                Next
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
              Keyboard: N = Next Creator
            </Typography>
          </>
        )}
      </Paper>
    </>
  );
}
